import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  HandsPraying,
  BookOpen,
  Heart,
  Notebook,
  Trash,
  Plus,
  SpinnerGap,
  BookmarkSimple,
} from "@phosphor-icons/react";

const typeIcons = {
  prayer: HandsPraying,
  verse: BookOpen,
  reflection: Heart,
  answered: HandsPraying,
};

const typeColors = {
  prayer: "text-amber-400",
  verse: "text-blue-400",
  reflection: "text-rose-400",
  answered: "text-emerald-400",
};

export default function JournalPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [newEntry, setNewEntry] = useState({ type: "reflection", title: "", content: "" });
  const [saving, setSaving] = useState(false);

  const fetchEntries = () => {
    setLoading(true);
    api.get("/journal")
      .then((res) => setEntries(res.data.entries))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchEntries(); }, []);

  const filtered = tab === "all" ? entries : entries.filter((e) => e.type === tab);

  const handleAdd = async () => {
    if (!newEntry.title.trim() || !newEntry.content.trim()) return;
    setSaving(true);
    try {
      await api.post("/journal", newEntry);
      toast.success("Entry saved!");
      setShowAdd(false);
      setNewEntry({ type: "reflection", title: "", content: "" });
      fetchEntries();
    } catch {
      toast.error("Failed to save entry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/journal/${id}`);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Entry removed");
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-3xl mx-auto" data-testid="journal-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl text-white font-medium">
            Faith Journal
          </h1>
          <p className="text-slate-400 font-body text-sm mt-1">
            Your saved prayers, verses, and reflections.
          </p>
        </div>
        <Button
          onClick={() => setShowAdd(!showAdd)}
          data-testid="add-journal-btn"
          className="bg-[#D4AF37] hover:bg-[#FDE047] text-[#020617] rounded-full font-body text-sm"
        >
          <Plus size={16} className="mr-1" />
          Add Entry
        </Button>
      </div>

      {/* Add Entry Form */}
      {showAdd && (
        <Card className="bg-[#0F172A] border-[#D4AF37]/20 mb-6 animate-fade-in" data-testid="add-entry-form">
          <CardContent className="p-5 space-y-3">
            <div className="flex gap-2">
              {["prayer", "verse", "reflection", "answered"].map((t) => (
                <button
                  key={t}
                  onClick={() => setNewEntry({ ...newEntry, type: t })}
                  className={`px-3 py-1.5 rounded-full text-xs font-body capitalize transition-all border ${
                    newEntry.type === t
                      ? "bg-[#D4AF37]/15 border-[#D4AF37]/40 text-[#D4AF37]"
                      : "border-white/10 text-slate-400 hover:border-white/20"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <Input
              value={newEntry.title}
              onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })}
              placeholder="Title"
              data-testid="journal-title-input"
              className="bg-[#1E293B] border-white/10 text-white font-body"
            />
            <textarea
              value={newEntry.content}
              onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
              placeholder="Write your thoughts..."
              rows={3}
              data-testid="journal-content-input"
              className="w-full bg-[#1E293B] border border-white/10 rounded-xl px-4 py-3 text-white text-sm font-body resize-none focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setShowAdd(false)} className="text-slate-400 font-body text-sm">
                Cancel
              </Button>
              <Button
                onClick={handleAdd}
                disabled={saving}
                data-testid="save-journal-btn"
                className="bg-[#D4AF37] hover:bg-[#FDE047] text-[#020617] rounded-xl font-body text-sm"
              >
                {saving ? <SpinnerGap size={16} className="animate-spin" /> : "Save"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList className="bg-[#1E293B] h-auto p-1 flex-wrap">
          {["all", "prayer", "verse", "reflection", "answered"].map((t) => (
            <TabsTrigger
              key={t}
              value={t}
              className="font-body text-xs capitalize data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617]"
            >
              {t}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Entries */}
      {loading ? (
        <div className="flex justify-center py-16">
          <SpinnerGap size={24} className="text-[#D4AF37] animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <BookmarkSimple size={40} weight="duotone" className="text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 font-body text-sm">No entries yet. Start a conversation with AI or add a reflection.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((entry) => {
            const Icon = typeIcons[entry.type] || Notebook;
            const color = typeColors[entry.type] || "text-slate-400";
            return (
              <Card key={entry.id} className="bg-[#0F172A] border-white/5 card-hover" data-testid={`journal-entry-${entry.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      <Icon size={18} weight="duotone" className={color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-white text-sm font-body font-medium truncate">
                          {entry.title}
                        </p>
                        <span className={`text-[10px] uppercase tracking-widest font-body ${color}`}>
                          {entry.type}
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm font-body line-clamp-2">
                        {entry.content}
                      </p>
                      <p className="text-slate-600 text-xs font-body mt-2">
                        {new Date(entry.created_at).toLocaleDateString("en-US", {
                          month: "short", day: "numeric", year: "numeric",
                        })}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      data-testid={`delete-entry-${entry.id}`}
                      className="text-slate-600 hover:text-red-400 transition-colors p-1"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
